import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import {
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ChevronRight,
  Bell
} from 'lucide-react';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Category colors
const CATEGORY_COLORS = {
  food: '#ef4444',
  groceries: '#22c55e',
  transport: '#3b82f6',
  entertainment: '#8b5cf6',
  shopping: '#f59e0b',
  utilities: '#06b6d4',
  healthcare: '#ec4899',
  travel: '#14b8a6',
  other: '#6b7280'
};

// Member colors (for charts)
const MEMBER_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', 
  '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6'
];

const FamilyDashboardWidget = ({ token, onOpenManagement }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [household, setHousehold] = useState(null);
  const [error, setError] = useState(null);
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First check if user has a household
      const householdRes = await fetch(`${API}/household`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (householdRes.ok) {
        const householdData = await householdRes.json();
        setHousehold(householdData);
        
        if (householdData.household && householdData.membership?.permissions?.view_family_dashboard) {
          // Fetch dashboard data
          const dashRes = await fetch(`${API}/household/dashboard`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (dashRes.ok) {
            const data = await dashRes.json();
            setDashboardData(data);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching family dashboard:', err);
      setError(err.message);
    }
    
    setLoading(false);
  }, [token]);
  
  useEffect(() => {
    if (token) {
      fetchDashboard();
    }
  }, [token, fetchDashboard]);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(lang === 'es' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // If no household or not admin, don't show
  if (!household?.household || !household?.membership?.permissions?.view_family_dashboard) {
    return null;
  }
  
  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }
  
  if (error || !dashboardData) {
    return null;
  }
  
  const { summary, by_category, by_member, budget_vs_actual, notifications } = dashboardData;
  
  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="w-5 h-5" />
            {lang === 'es' ? 'Panel Familiar' : 'Family Dashboard'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onOpenManagement}>
            {lang === 'es' ? 'Gestionar' : 'Manage'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <p className="text-sm text-blue-700">
          {household.household.name} • {household.members?.length || 0} {lang === 'es' ? 'miembros' : 'members'}
        </p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <DollarSign className="w-5 h-5 text-red-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{formatCurrency(summary?.total_spending || 0)}</p>
            <p className="text-xs text-gray-500">{lang === 'es' ? 'Este Mes' : 'This Month'}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <BarChart3 className="w-5 h-5 text-blue-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{summary?.expense_count || 0}</p>
            <p className="text-xs text-gray-500">{lang === 'es' ? 'Gastos' : 'Expenses'}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <Users className="w-5 h-5 text-green-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-gray-900">{summary?.member_count || 0}</p>
            <p className="text-xs text-gray-500">{lang === 'es' ? 'Activos' : 'Active'}</p>
          </div>
        </div>
        
        {/* Spending by Member */}
        {by_member && by_member.length > 0 && (
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {lang === 'es' ? 'Gastos por Miembro' : 'Spending by Member'}
            </h4>
            <div className="space-y-2">
              {by_member.slice(0, 4).map((member, index) => {
                const maxAmount = Math.max(...by_member.map(m => m.amount));
                const percentage = maxAmount > 0 ? (member.amount / maxAmount) * 100 : 0;
                return (
                  <div key={member.user_id} className="flex items-center gap-2">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
                    />
                    <span className="text-xs text-gray-600 w-24 truncate">{member.name}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: MEMBER_COLORS[index % MEMBER_COLORS.length]
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium text-gray-700 w-16 text-right">
                      {formatCurrency(member.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Budget Alerts */}
        {budget_vs_actual && budget_vs_actual.length > 0 && (
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {lang === 'es' ? 'Estado del Presupuesto' : 'Budget Status'}
            </h4>
            <div className="space-y-2">
              {budget_vs_actual.slice(0, 3).map((budget) => {
                const isOver = budget.status === 'over';
                const isWarning = budget.status === 'warning';
                return (
                  <div key={budget.category} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isOver ? (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      ) : isWarning ? (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <span className="text-xs text-gray-600">{budget.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${
                        isOver ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {budget.percent}%
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatCurrency(budget.actual)}/{formatCurrency(budget.budget)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Notifications */}
        {notifications && notifications.length > 0 && (
          <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">
                {lang === 'es' ? 'Alertas' : 'Alerts'}
              </span>
            </div>
            {notifications.slice(0, 2).map((notif) => (
              <p key={notif.id} className="text-xs text-yellow-700">
                • {lang === 'es' ? notif.message_es : notif.message}
              </p>
            ))}
          </div>
        )}
        
        {/* Top Categories */}
        {by_category && by_category.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {by_category.slice(0, 5).map((cat) => (
              <div 
                key={cat.category}
                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.other }}
              >
                {cat.category}: {formatCurrency(cat.amount)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FamilyDashboardWidget;
